import React from 'react';
import { cn } from '@/lib/utils';

const linkClass =
  'text-muted-foreground underline-offset-2 transition hover:text-foreground/85 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8B7355]/30 rounded-sm';

function HeldItem({ count, label, onClick, className }) {
  const content = (
    <>
      <span className="text-foreground/75 tabular-nums">{count}</span> {label}
    </>
  );

  if (typeof onClick === 'function') {
    return (
      <button type="button" onClick={onClick} className={cn(linkClass, 'whitespace-nowrap', className)}>
        {content}
      </button>
    );
  }

  return <span className={className}>{content}</span>;
}

/**
 * Quiet, clickable archive depth shortcuts — stories, voice, documents.
 */
export function ArchiveHeldHereLinks({
  storyCount = 0,
  voiceMemoriesCount = 0,
  documentCount = 0,
  mentionedStoriesCount = 0,
  onOpenStories,
  onOpenVoice,
  onOpenDocuments,
  variant = 'inline',
  className,
  listLabel = 'Held here',
  showMentions = false,
}) {
  const storyLabel = storyCount === 1 ? 'story' : 'stories';
  const voiceLabel = voiceMemoriesCount === 1 ? 'voice memory' : 'voice memories';
  const docLabel = documentCount === 1 ? 'document' : 'documents';
  const mentionLabel = mentionedStoriesCount === 1 ? 'family story' : 'family stories';

  if (variant === 'list') {
    return (
      <div className={className} data-testid="archive-held-here-links">
        {listLabel ? (
          <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground/45 mb-2">
            {listLabel}
          </p>
        ) : null}
        <ul className="space-y-1.5 font-serif text-[13px] text-muted-foreground/90">
          <li>
            <HeldItem count={storyCount} label={storyLabel} onClick={onOpenStories} />
          </li>
          <li>
            <HeldItem count={voiceMemoriesCount} label={voiceLabel} onClick={onOpenVoice} />
          </li>
          <li>
            <HeldItem count={documentCount} label={docLabel} onClick={onOpenDocuments} />
          </li>
          {showMentions ? (
            <li>
              Mentioned in{' '}
              <span className="text-foreground/80 tabular-nums">{mentionedStoriesCount}</span> {mentionLabel}
            </li>
          ) : null}
        </ul>
      </div>
    );
  }

  return (
    <p
      className={cn('text-[11px] leading-relaxed text-muted-foreground sm:text-xs flex flex-wrap items-center gap-x-1 gap-y-0.5', className)}
      data-testid="archive-held-here-links"
    >
      <HeldItem count={storyCount} label={storyLabel} onClick={onOpenStories} />
      <span className="mx-1 opacity-40" aria-hidden>
        ·
      </span>
      <HeldItem count={voiceMemoriesCount} label={voiceLabel} onClick={onOpenVoice} />
      <span className="mx-1 opacity-40" aria-hidden>
        ·
      </span>
      <HeldItem count={documentCount} label={docLabel} onClick={onOpenDocuments} />
    </p>
  );
}

export default ArchiveHeldHereLinks;
